import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  HttpResponse,
  LeaveReportSummary,
  HoursReportSummary,
  PendingTimesheetRow,
  ProjectReportSummary,
  CustomerReportRow,
  ReportFilterParams,
  UserHttpService,
} from '../public-api';

/**
 * Service for the PM Reports dashboard endpoints.
 * Base URL (environment.baseUrl) is prepended by the HTTP interceptor, so only
 * the relative endpoint path is provided here.
 */
@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(
    private http: HttpClient,
    private userSessionService: UserHttpService
  ) {}

  /** Appends the standard auth/identity fields required by every report endpoint. */
  private baseFormData(): FormData {
    const formData = new FormData();
    const user = this.userSessionService.getUserInfo;
    formData.append('user_id', user.user_id);
    formData.append('usr_role_track_id', user.usr_role_track_id);
    formData.append('usr_customer_track_id', user.usr_customer_track_id);
    formData.append('token', this.userSessionService.userToken);
    return formData;
  }

  /** reports/leaveReport — Attendance / Leaves summary card. */
  getLeaveReport(filters?: ReportFilterParams): Observable<HttpResponse<LeaveReportSummary[]>> {
    const formData = this.withFilters(this.baseFormData(), filters);
    return this.http.post<HttpResponse<LeaveReportSummary[]>>('reports/leaveReport', formData);
  }

  /** reports/hoursReport — Hours Worked card. */
  getHoursReport(filters?: ReportFilterParams): Observable<HttpResponse<HoursReportSummary>> {
    const formData = this.baseFormData();
    if (filters?.month) {
      formData.append('month', filters.month);
    }
    if (filters?.range) {
      formData.append('range', filters.range);
    }
    if (filters?.project_track_id) {
      formData.append('project_track_id', filters.project_track_id);
    }
    return this.http.post<HttpResponse<HoursReportSummary>>('reports/hoursReport', formData);
  }

  /** reports/pendingTimesheet — Team's Timesheet section. */
  getPendingTimesheet(): Observable<HttpResponse<PendingTimesheetRow[]>> {
    return this.http.post<HttpResponse<PendingTimesheetRow[]>>(
      'reports/pendingTimesheet',
      this.baseFormData()
    );
  }

  /** reports/CustomerReport — Customers summary card. */
  getCustomerReport(filters?: ReportFilterParams): Observable<HttpResponse<CustomerReportRow[]>> {
    const formData = this.baseFormData();
    if (filters?.client_id) {
      formData.append('client_id', filters.client_id);
    }
    if (filters?.project_type) {
      formData.append('project_type', filters.project_type);
    }
    if (filters?.project_id) {
      formData.append('project_id', filters.project_id);
    }
    return this.http.post<HttpResponse<CustomerReportRow[]>>('reports/CustomerReport', formData);
  }

  /** reports/projectReport — Projects summary card. */
  getProjectReport(
    filters?: ReportFilterParams
  ): Observable<HttpResponse<ProjectReportSummary | ProjectReportSummary[]>> {
    return this.http.post<HttpResponse<ProjectReportSummary | ProjectReportSummary[]>>(
      'reports/projectReport',
      this.withFilters(this.baseFormData(), filters)
    );
  }

  private withFilters(formData: FormData, filters?: ReportFilterParams): FormData {
    if (!filters) {
      return formData;
    }
    if (filters.month) {
      formData.append('month', filters.month);
    }
    if (filters.type) {
      formData.append('type', filters.type);
    }
    if (filters.daterange) {
      formData.append('daterange', filters.daterange);
    }
    if (filters.project_id) {
      formData.append('project_id', filters.project_id);
    }
    if (filters.client_id) {
      formData.append('client_id', filters.client_id);
    }
    if (filters.status) {
      formData.append('status', filters.status);
    }
    if (filters.project_type) {
      formData.append('project_type', filters.project_type);
    }
    if (filters.industry) {
      formData.append('industry', filters.industry);
    }
    return formData;
  }
}
